import React, { use, useCallback, useMemo } from "react";
import {
  useNavigate,
  useParams,
  Link,
  useSearchParams,
} from "react-router-dom";
import {
  NMForm,
  Row,
  Col,
  Button,
  TextButton,
  NMWaitIndicator,
} from "@neumetric/blueprint-lib/components";
import { useDispatch, useSelector } from "react-redux";
import {
  auditActions,
  getReportFormatsList,
  getBlueprintModelsList,
  getAuditTypesList,
  getGovernorsList,
  getUsersList,
  getCurrentAuditDetails,
} from "../../store";
import { getCurrentUser, isAuditee } from "@neumetric/blueprint-lib/store";
import { NewAuditFormFields } from "./Helper";
import { DateTimeUtil } from "@neumetric/blueprint-lib/utility";

const toIdArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  return [val];
};

const pickId = (x) => {
  if (x == null) return null;
  if (typeof x === "object") return x.id ?? x.value ?? x.key ?? null;
  return x;
};

const buildCreateAuditPayloadFromValues = (
  modifiedFields,
  values,
  currentUser,
  isCreate,
) => {
  const blueprintId = pickId(values?.blueprint);
  const governorIds = toIdArray(values?.governor).map(pickId).filter(Boolean);
  const userIds = toIdArray(values?.user).map(pickId).filter(Boolean);
  const asmt = {};
  modifiedFields.forEach((field) => {
    switch (field.attribute) {
      case "governor":
        asmt.asmt_assessors_attributes = governorIds.map((id) => ({
          assessor_id: Number(id),
        }));
        break;
      case "user":
        asmt.asmt_auditees_attributes = userIds.map((id) => ({
          auditee_id: Number(id),
        }));
        break;

      default:
        asmt[field.attribute] = values[field.attribute];
        break;
    }
  });
  if (isCreate) {
    asmt.mapped_asmt_auditees_attributes = [];
    asmt.customer_id = currentUser?.org?.id;
    asmt.start_date = values.start_date;
    asmt.end_date = values.end_date;
  }
  return { asmt };
};

export const CreateAuditScreen = () => {
  const _formRef = React.useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reportFormats = useSelector(getReportFormatsList);
  const blueprintModels = useSelector(getBlueprintModelsList);
  const auditTypes = useSelector(getAuditTypesList);
  const governors = useSelector(getGovernorsList);
  const users = useSelector(getUsersList);
  const currentUser = useSelector(getCurrentUser);
  const currentAuditDetails = useSelector(getCurrentAuditDetails) || null;
  const [canSubmit, setCanSubmit] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const { auditId } = useParams();
  const [searchParams] = useSearchParams();
  const blueprintId = searchParams.get("blueprint_id");

  const mode = auditId ? "edit" : "create";
  const showForm = useMemo(() => {
    if (mode === "edit") {
      return Boolean(currentAuditDetails);
    } else if (mode === "create") {
      if (blueprintId) {
        return (blueprintModels || []).length > 0;
      }
      return true;
    }
  }, [mode, currentAuditDetails, blueprintId, blueprintModels]);

  const formData = useMemo(() => {
    let initialData = {};
    if (showForm) {
      if (mode === "edit") {
        initialData = {
          ...currentAuditDetails,
          lib1_model_id: Number(currentAuditDetails?.blueprint),
          desc: currentAuditDetails.description,
        };
      } else {
        const start = new Date(),
          endDate = new Date();
        endDate.setDate(start.getDate() + 7);
        initialData.start_date = start.toISOString();
        initialData.end_date = endDate.toISOString();
      }
    }
    return initialData;
  }, [showForm, mode, currentAuditDetails, blueprintId, blueprintModels]);

  React.useEffect(() => {
    if (isAuditee(currentUser)) {
      navigate("/not-found");
      return;
    }
    dispatch(auditActions.fetchReportFormats());
    dispatch(auditActions.fetchBlueprintModels());
    dispatch(auditActions.fetchAuditTypes());
    dispatch(auditActions.fetchGovernors());
    dispatch(auditActions.fetchUsers());
  }, []);

  React.useEffect(() => {
    if (mode === "create" && blueprintId && showForm) {
      const selectedModel = blueprintModels.find(
        (bp) => bp.guid === blueprintId,
      );
      if (!selectedModel) {
        navigate("/not-found");
      }
      const updatedData = {
        title: `${selectedModel.name} [${new DateTimeUtil().toDateString("DDMMYYYY")}]`,
        desc: selectedModel.desc || "",
        lib1_model_id: Number(selectedModel.id),
      };
      _formRef.current?.updateFormData?.(updatedData);
    }
  }, [blueprintId, showForm, blueprintModels, mode]);

  React.useEffect(() => {
    const ok = _formRef.current?.validate?.(false) === true;
    setCanSubmit(ok);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);

  React.useEffect(() => {
    if (mode !== "edit") return;
    dispatch(auditActions.fetchAuditDetails({ auditId }));
  }, [mode, auditId, dispatch]);

  React.useEffect(() => {
    if (mode !== "edit" || !currentAuditDetails) return;

    const status = String(currentAuditDetails.status || "")
      .trim()
      .toLowerCase();

    // Details have not loaded completely yet
    if (!status) return;

    if (!["pending", "submitted", "revision"].includes(status)) {
      navigate("/page-not-found", { replace: true });
    }
  }, [currentAuditDetails, mode, navigate]);

  const getOptions = React.useCallback(
    (attribute) => {
      if (attribute === "report_format") {
        return (reportFormats || []).map((rf) => ({
          id: String(rf),
          label: rf,
        }));
      }
      if (attribute === "lib1_model_id") {
        return (blueprintModels || []).map((bm) => ({
          id: Number(bm.id),
          label: bm.name,
        }));
      }
      if (attribute === "audit_type") {
        return (auditTypes || []).map((a) => ({ id: a.key, label: a.label }));
      }
      if (attribute === "governor") {
        return (governors || []).map((g) => ({
          id: String(g.id),
          label: g.name,
        }));
      }
      if (attribute === "user") {
        return (users || []).map((u) => ({ id: String(u.id), label: u.name }));
      }
      return [];
    },
    [reportFormats, blueprintModels, auditTypes, governors, users],
  );
  const handleChange = (attribute, value) => {
    if (attribute === "lib1_model_id" && mode === "create") {
      const model = blueprintModels.find(
        (bm) => String(bm.id) === String(value),
      );
      if (model) {
        _formRef.current.updateFormData({
          title: `${model.name} [${new DateTimeUtil().toDateString("DDMMYYYY")}]`,
          desc: model.desc || "",
        });
      }
    }
    const timer = setTimeout(() => {
      clearTimeout(timer);
      const ok = _formRef.current?.validate?.(false) === true;
      setCanSubmit(ok);
    }, 500);
  };

  const handleSubmit = () => {
    if (submitting) return;
    const values = _formRef.current?.getValues?.() || {};
    const modifiedFields = _formRef.current.getDirtyFields();
    const payload = buildCreateAuditPayloadFromValues(
      modifiedFields,
      values,
      currentUser,
      mode === "create",
    );

    setSubmitting(true);

    const action =
      mode === "edit"
        ? auditActions.updateAudit({ auditId, payload })
        : auditActions.createAudit({ payload });

    action.onSuccess = () => {
      navigate("/A/T"); // only navigate
    };

    action.onFinally = () => {
      setSubmitting(false); // always stop spinner
    };

    dispatch(action);
  };

  const getFormFields = () => {
    const fields = NewAuditFormFields.map((_) => ({ ..._ }));
    if (mode === "create") {
      fields[4].min = new Date();
      fields[5].min = new Date();
    }
    return fields;
  };
  const pageTitle = mode === "edit" ? "Update Audit" : "Create New Audit";
  const primaryLabel = mode === "edit" ? "Update Audit" : "Create Audit";
  console.log("formData", formData);

  return (
    <div className="w-100 h-100 mt-20 ml-20 overflow-auto">
      <Link className="mt-4 mb-10" to={"/A/T"}>
        <span className="fs-14 fw-400 text-action">Tracker</span>
      </Link>
      <div className="ff-exo2 mt-10 fs-20 fw-600 mb-40 text-heading">
        {pageTitle}{" "}
      </div>

      <Col className="ml-20 flex-auto mb-40" style={{ maxWidth: "880px" }}>
        {mode === "edit" && !currentAuditDetails && (
          <NMWaitIndicator size="small" />
        )}
        {showForm && (
          <>
            <NMForm
              ref={_formRef}
              width={850}
              variant="INFO"
              fields={getFormFields()}
              onChange={handleChange}
              formData={formData}
              getOptions={getOptions}
            />
            <Row className="pl-30 mt-10 w-100 gap-10" justify="end">
              <TextButton
                icon="CANCEL"
                label="Cancel"
                variant="ACTION"
                disabled={submitting}
                onClick={() => navigate(`/A/T`)}
              />
              <Button
                icon="SQUARE_NEXT"
                label={primaryLabel}
                onClick={handleSubmit}
                disabled={!canSubmit}
                loading={submitting}
              />
            </Row>
          </>
        )}
      </Col>
    </div>
  );
};
